import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { Toaster } from './components/ui/sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import VirtualQueue from './components/VirtualQueue';
import { Loader2 } from 'lucide-react';
import { ErrorBoundary } from './components/ErrorBoundary';

// Critical pages - loaded immediately
import Landing from './pages/Landing';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import GoogleCallbackPage from './pages/auth/GoogleCallbackPage';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';

// Lazy loaded pages - loaded on demand
const EventDetail = lazy(() => import('./pages/EventDetail'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const RankingPage = lazy(() => import('./pages/RankingPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminEvents = lazy(() => import('./pages/admin/AdminEvents'));
const AdminEventStats = lazy(() => import('./pages/admin/AdminEventStats'));
const AdminQuestions = lazy(() => import('./pages/admin/AdminQuestions'));
const AdminShopify = lazy(() => import('./pages/admin/AdminShopify'));
const QueuePreview = lazy(() => import('./pages/QueuePreview'));
const QueueMonitor = lazy(() => import('./pages/QueueMonitor'));
const GamePage = lazy(() => import('./pages/GamePage'));
const AdminGame = lazy(() => import('./pages/admin/AdminGame'));

function LazyFallback() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        </div>
    );
}

import './App.css';

// Router component
function AppRouter() {
    const { isAuthenticated, loading } = useAuth();

    return (
        <Suspense fallback={<LazyFallback />}>
        <Routes>
            {/* Public Routes */}
            <Route 
                path="/" 
                element={
                    !loading && isAuthenticated 
                        ? <Navigate to="/dashboard" replace /> 
                        : <Landing />
                } 
            />

            {/* Auth Routes */}
            <Route 
                path="/login" 
                element={
                    !loading && isAuthenticated 
                        ? <Navigate to="/dashboard" replace /> 
                        : <LoginPage />
                } 
            />
            <Route 
                path="/register" 
                element={
                    !loading && isAuthenticated 
                        ? <Navigate to="/dashboard" replace /> 
                        : <RegisterPage />
                } 
            />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />

            {/* Protected Client Routes */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/profile" 
                element={
                    <ProtectedRoute>
                        <Profile />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/event/:eventId" 
                element={
                    <ProtectedRoute>
                        <EventDetail />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/history" 
                element={
                    <ProtectedRoute>
                        <HistoryPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/ranking" 
                element={
                    <ProtectedRoute>
                        <RankingPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/game" 
                element={
                    <ProtectedRoute>
                        <GamePage />
                    </ProtectedRoute>
                } 
            />

            {/* Admin Routes - Separate Auth */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="events" element={<AdminEvents />} />
                <Route path="events/:eventId/stats" element={<AdminEventStats />} />
                <Route path="questions" element={<AdminQuestions />} />
                <Route path="shopify" element={<AdminShopify />} />
                <Route path="monitor" element={<QueueMonitor />} />
                <Route path="game" element={<AdminGame />} />
            </Route>

            {/* Queue Preview (temp) */}
            <Route path="/queue-preview" element={<QueuePreview />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
    );
}

function App() {
    return (
        <ErrorBoundary>
            <ThemeProvider>
                <AuthProvider>
                    <AdminAuthProvider>
                        <BrowserRouter>
                            <VirtualQueue>
                                <AppRouter />
                            </VirtualQueue>
                            <Toaster position="top-center" richColors />
                        </BrowserRouter>
                    </AdminAuthProvider>
                </AuthProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
